/**
 * What a space's sidebar row expands to (RFC 0281 §7, S5).
 *
 * A space stores its tree as the content url, so the ids under its chevron are
 * the leaves of that tree, decoded. This is the whole of the mapping from "a
 * space" to "the rows beneath it", kept pure so the sidebar can be tested
 * without a repo and so the decode is exercised on the strings spaces actually
 * hold rather than only on the ones the layout writes.
 *
 * THIS REPLACES RFC 0280's GROUP. A group was a hand made list of ids beside
 * the working set; a space is a named arrangement you can switch to, and it
 * already holds ids. Keeping both would mean filing something and opening it
 * were separate acts with separate homes (§7).
 */

import { leaves } from '../../features/code/contentTree'
import { decodeContent } from '../window/contentUrl'

/**
 * The subject ids a space holds, in visual order, deduplicated.
 *
 * SESSIONS AND DOCUMENTS ONLY. A picker has no subject, and a terminal's is a
 * process on a machine rather than anything the sidebar's catalog can resolve,
 * so neither contributes a row: `resolveHomeItems` would drop them anyway and
 * an id it silently discards is worse than one never offered.
 *
 * A space whose tree is empty or unparseable yields nothing rather than
 * throwing. An unparseable tree is a link written before the project segment
 * existed (`contentUrl.ts`), which is a row that cannot be rendered, not an
 * error to show somebody.
 */
export function spaceItemIds(content: string): string[] {
  const tree = decodeContent(content)
  if (!tree) return []

  const out: string[] = []
  const seen = new Set<string>()
  for (const leaf of leaves(tree)) {
    const { content: c } = leaf
    const id =
      c.kind === 'agent' ? c.sessionId : c.kind === 'document' ? c.docId : null
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}

/**
 * What a space is called on screen.
 *
 * A NAME ONCE GIVEN IS KEPT. A space nobody named is labelled by what it holds:
 * the first session with a title, else the first document with one, so it reads
 * as its work without a rename. The label is never written back, so it follows
 * the space's contents until a person or an agent names it, and that pins it.
 */
export function spaceLabel(
  space: { name: string; content: string },
  titleOf: (kind: 'session' | 'document', id: string) => string | undefined,
): string {
  const named = space.name.trim()
  if (named) return named
  const tree = decodeContent(space.content)
  if (!tree) return 'Untitled space'
  const titled = (kind: 'session' | 'document') => {
    for (const { content: c } of leaves(tree)) {
      const id =
        kind === 'session' && c.kind === 'agent'
          ? c.sessionId
          : kind === 'document' && c.kind === 'document'
            ? c.docId
            : null
      const title = id ? titleOf(kind, id)?.trim() : undefined
      if (title) return title
    }
    return undefined
  }
  return titled('session') ?? titled('document') ?? 'Untitled space'
}

/**
 * The session ids a space holds, in visual order, deduplicated.
 *
 * SESSIONS ONLY, unlike `spaceItemIds`: this feeds a status read that opens
 * each id as a session document, and a document id handed to it would be opened
 * as something it is not.
 */
export function spaceSessionIds(content: string): string[] {
  const tree = decodeContent(content)
  if (!tree) return []
  const out = new Set<string>()
  for (const { content: c } of leaves(tree))
    if (c.kind === 'agent' && c.sessionId) out.add(c.sessionId)
  return [...out]
}

/** What a space's button says about the sessions open in it (RFC 0330 3a). */
export type SpaceActivity = 'asking' | 'working' | undefined

/**
 * The most urgent status among a space's sessions.
 *
 * ASKING WINS: it is the one that needs you. `queued` counts as working, as it
 * does in the session rows' "working": somebody asked and it has not finished.
 * An id with no status is UNKNOWN and says nothing, never "working", or every
 * space would light on load before a single status was read.
 */
export function spaceActivity(
  sessionIds: readonly string[],
  statuses: ReadonlyMap<string, string>,
): SpaceActivity {
  let working = false
  for (const id of sessionIds) {
    const s = statuses.get(id)
    if (s === 'asking') return 'asking'
    if (s === 'running' || s === 'queued') working = true
  }
  return working ? 'working' : undefined
}

/**
 * The sessions each space holds, for its dot (RFC 0330 3b).
 *
 * TWO SOURCES, because a person reads two. The panes the space shows, from its
 * stored tree, and the sidebar's Open list for that space: a session you opened
 * here and then replaced with a document is still in the list under the space,
 * still yours in this space, and its dot has to say so or the row lies.
 *
 * The Open list holds documents too, so only ids `isSession` accepts are kept.
 * The CURRENT space's list comes from the live hook (`currentOpenIds`), which
 * is ahead of what storage has; every other space's from `openIdsOf`.
 */
export function spaceSessions(
  spaces: readonly { spaceId: string; content: string }[],
  currentSpaceId: string | null,
  currentOpenIds: readonly string[],
  openIdsOf: (spaceId: string) => readonly string[],
  isSession: (id: string) => boolean,
): { spaceId: string; sessionIds: string[] }[] {
  return spaces.map((sp) => {
    const open =
      sp.spaceId === currentSpaceId ? currentOpenIds : openIdsOf(sp.spaceId)
    const ids = new Set(spaceSessionIds(sp.content))
    for (const id of open) if (isSession(id)) ids.add(id)
    return { spaceId: sp.spaceId, sessionIds: [...ids] }
  })
}

/**
 * Which sessions to open for a status, most important first, under `limit`
 * (RFC 0330 3b).
 *
 * The current space's sessions, then every other space's in strip order, then
 * the picker's rows. Anything the registry already answers is left out: it is
 * the free path, and this list is the one that pays a document per id.
 */
export function statusReadOrder(
  spaces: readonly { spaceId: string; sessionIds: readonly string[] }[],
  currentSpaceId: string | null,
  pickerIds: readonly string[],
  answered: ReadonlySet<string> | ReadonlyMap<string, unknown>,
  limit: number,
): string[] {
  const ordered = [
    ...spaces.filter((sp) => sp.spaceId === currentSpaceId),
    ...spaces.filter((sp) => sp.spaceId !== currentSpaceId),
  ]
  const out: string[] = []
  const seen = new Set<string>()
  const take = (id: string) => {
    if (out.length >= limit || seen.has(id) || answered.has(id)) return
    seen.add(id)
    out.push(id)
  }
  for (const sp of ordered) for (const id of sp.sessionIds) take(id)
  for (const id of pickerIds) take(id)
  return out
}

/** How many panes a space holds, which is what its row counts. */
export function spacePaneCount(content: string): number {
  const tree = decodeContent(content)
  return tree ? leaves(tree).length : 0
}

/**
 * The space a session is ACTIVE in, for putting you through to it (RFC 0336
 * 3d): where the window goes so you talk to it in its own space, not yours.
 *
 * STRONG SIGNALS FIRST, the space you are in winning a tie: a space whose
 * composer is talking to it, then a space showing it as a pane. Only then the
 * Open list, which is history (everything ever opened there, up to a cap), so
 * a session merely visited here does not keep you here when it lives as a pane
 * or a conversation somewhere else. Null when no space has it at all.
 */
export function homeSpaceOf(
  sessionId: string,
  opts: {
    currentSpaceId: string | null
    /** Each space's composer recipient, as the window remembers it. */
    chosen: Readonly<Record<string, string | null>>
    spaces: readonly { spaceId: string; content: string }[]
    /** Panes plus Open list per space (`spaceSessions`). */
    sessionsBySpace: readonly { spaceId: string; sessionIds: string[] }[]
  },
): string | null {
  const { currentSpaceId, chosen, spaces, sessionsBySpace } = opts
  const known = new Set(spaces.map((sp) => sp.spaceId))
  const talking = (spaceId: string) => chosen[spaceId] === sessionId
  const showing = (sp: { content: string }) =>
    spaceSessionIds(sp.content).includes(sessionId)
  const listing = (spaceId: string) =>
    sessionsBySpace
      .find((sp) => sp.spaceId === spaceId)
      ?.sessionIds.includes(sessionId) ?? false
  const current = spaces.find((sp) => sp.spaceId === currentSpaceId)
  // 1. A conversation: the space whose composer is talking to it.
  if (currentSpaceId !== null && talking(currentSpaceId)) return currentSpaceId
  for (const spaceId of Object.keys(chosen))
    if (known.has(spaceId) && talking(spaceId)) return spaceId
  // 2. A pane.
  if (current && showing(current)) return current.spaceId
  for (const sp of spaces) if (showing(sp)) return sp.spaceId
  // 3. History: opened there before.
  if (currentSpaceId !== null && listing(currentSpaceId)) return currentSpaceId
  for (const sp of sessionsBySpace)
    if (known.has(sp.spaceId) && listing(sp.spaceId)) return sp.spaceId
  return null
}
